import React, { useState, useMemo, useEffect } from "react";
import PageLayout from "@/components/PageLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import {
  Github,
  ExternalLink,
  ArrowRight,
  Search,
  Filter,
  Figma,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { useProjectsData } from "@/hooks/useProjectsData";
import { Project } from "@/types/types";
import { useProjects } from "@/context/ProjectsContext";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import Lottie from "lottie-react";
import { Helmet } from "react-helmet";

const Projects = () => {
  const { state, dispatch } = useProjects();
  const { projects, isLoading, isError, error, stats } = useProjectsData();

  // Move all useState declarations to the top
  const [showProjectDetail, setShowProjectDetail] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  const filteredProjects = useMemo(() => {
    return projects.filter((project) => {
      // Apply search filter
      const matchesSearch =
        searchQuery === "" ||
        project.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        project.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        project.tags.some((tag) =>
          tag.toLowerCase().includes(searchQuery.toLowerCase())
        );

      // Apply status filter
      const matchesStatus =
        statusFilter === null || project.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [projects, searchQuery, statusFilter]);



  const handleProjectClick = (project: Project) => {
    dispatch({ type: "SELECT_PROJECT", payload: project });
    setShowProjectDetail(true);
  };
  // Filter projects based on search query and status filter

  // Project detail view
  if (showProjectDetail && state.selectedProject) {
    return (
      <PageLayout
        title={state.selectedProject.title}
        description={state.selectedProject.description}
      >
        {/* Project detail view remains unchanged */}
        <div className="space-y-8">
          <div className="relative aspect-video rounded-lg overflow-hidden">
            <img
              src={state.selectedProject.image}
              alt={state.selectedProject.title}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Rest of the detail view remains unchanged */}
          <div className="flex flex-wrap gap-2 mb-4">
            {state.selectedProject.tags.map((tag, index) => (
              <Badge key={index} variant="secondary">
                {tag}
              </Badge>
            ))}
          </div>

          {/* ... other detail view elements ... */}

          <Button
            variant="ghost"
            onClick={() => {
              setShowProjectDetail(false);
              dispatch({ type: "SELECT_PROJECT", payload: null });
            }}
            className="mt-8"
          >
            Retourner
          </Button>
        </div>
      </PageLayout>
    );
  }

  return (
    <>
    <Helmet>
    <title>Mopaossi | Portfolio</title>
    <meta
      name="description"
      content="Développeur Frontend | No-code| Low-code | UI/UX | Agilité"
    />
    <meta property="og:title" content="Mopaossi | Développeur Créatif" />
    <meta
      property="og:description"
      content="Découvrer un créateur, digitalisation, tech, app, dev : Mes sujets préférés."
    />
    <meta property="og:type" content="website" />
    <meta property="og:image" content="https://hynmctzoemxttjsmvjyz.supabase.co/storage/v1/object/public/images//sp.png" />
    <link rel="canonical" href="https://cbenji.pages.dev" />
  </Helmet>
    <PageLayout
      title="Mopaossi | Projets"
      description="Des projets ? voici quelques projets importants de mon parcours IT."
    >
      {isError ? (
        <div className="min-h-[60vh] flex items-center justify-center text-center">
          <p className="text-red-500 text-lg">
            Error loading projects: {error.message}
          </p>
        </div>
      ) : isLoading ? (
        // Skeleton loading state
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Left column - Projects skeleton */}
          <div className="lg:col-span-3 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
              {[...Array(6)].map((_, index) => (
                <Card key={index} className="overflow-hidden">
                  <div className="aspect-video relative overflow-hidden">
                    <Skeleton className="w-full h-full" />
                  </div>
                  <CardHeader className="pb-2">
                    <Skeleton className="h-6 w-3/4" />
                  </CardHeader>
                  <CardContent className="pb-2 space-y-4">
                    <Skeleton className="h-2 w-full" />
                    <div className="flex flex-wrap gap-1 mb-3">
                      <Skeleton className="h-5 w-16" />
                      <Skeleton className="h-5 w-16" />
                      <Skeleton className="h-5 w-16" />
                    </div>
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                  </CardContent>
                  <CardFooter className="pt-0">
                    <div className="flex justify-between items-center w-full">
                      <div className="flex gap-2">
                        <Skeleton className="h-8 w-8 rounded-full" />
                        <Skeleton className="h-8 w-8 rounded-full" />
                      </div>
                      <Skeleton className="h-8 w-20" />
                    </div>
                  </CardFooter>
                </Card>
              ))}
            </div>
          </div>

          {/* Right column - Stats skeleton */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <Skeleton className="h-4 w-32" />
              </CardHeader>
              <CardContent className="flex justify-center">
                <Skeleton className="w-32 h-32 rounded-full" />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Skeleton className="h-20 w-full" />
                  <Skeleton className="h-20 w-full" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Skeleton className="h-20 w-full" />
                  <Skeleton className="h-20 w-full" />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      ) : projects.length === 0 ? (
        <div className="min-h-[60vh] flex items-center justify-center text-center">
          <p className="text-gray-500 text-lg">Aucun projet trouvé.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Search and filter controls */}
          <div className="flex flex-col sm:flex-row gap-4 items-center">
            <div className="relative w-full sm:w-auto flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
              <Input
                type="text"
                placeholder="Search projects..."
                className="pl-8 w-full"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2">
                  <Filter className="h-4 w-4" />
                  {statusFilter ? (
                    <span>
                      Status:{" "}
                      {statusFilter === "completed"
                        ? "Terminé"
                        : statusFilter === "in-progress"
                        ? "En cours"
                        : statusFilter === "paused"
                        ? "Pause"
                        : ""}
                    </span>
                  ) : (
                    <span>Filtrer par status</span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setStatusFilter(null)}>
                  Tous les projects
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter("completed")}>
                  <Badge variant="default" className="mr-2">
                    Terminé
                  </Badge>
                  Terminé
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setStatusFilter("in-progress")}
                >
                  <Badge variant="default" className="mr-2">
                    En cours
                  </Badge>
                  En cours
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter("paused")}>
                  <Badge variant="default" className="mr-2">
                    Pause
                  </Badge>
                  Pause
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {statusFilter && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setStatusFilter(null)}
                className="gap-1"
              >
               Supprimer le filtre
              </Button>
            )}
          </div>

          {/* Results count */}
          <div className="text-sm text-gray-500">
             {filteredProjects.length} sur {projects.length} projets
            {statusFilter && (
              <span>
                {" "}
                filterer par status:{" "}
                <Badge
                  variant={
                    statusFilter === "completed"
                      ? "default"
                      : statusFilter === "in-progress"
                      ? "secondary"
                      : "destructive"
                  }
                  className="ml-1"
                >
                  {statusFilter === "completed"
                    ? "Terminé"
                    : statusFilter === "in-progress"
                    ? "En cours"
                    : statusFilter === "paused"
                    ? "Pause"
                    : ""}
                </Badge>
              </span>
            )}
            {searchQuery && <span> avec "{searchQuery}"</span>}
          </div>

          {/* Main grid layout */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Left column - Projects */}
            <div className="lg:col-span-3 space-y-4">
              {filteredProjects.length === 0 ? (
                <div className="min-h-[40vh] flex items-center justify-center text-center border rounded-lg p-8">
                  <div className="space-y-2">
                    <p className="text-gray-500 text-lg">
                      Aucun projet retrouvé 
                      </p>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSearchQuery("");
                        setStatusFilter(null);
                      }}
                    >
                      Effacer
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                  {filteredProjects.map((project, index) => (
                    <Card
                      key={index}
                      className="overflow-hidden transition-all duration-300 hover:shadow-lg cursor-pointer"
                      onClick={() => handleProjectClick(project)}
                    >
                      <div className="aspect-video relative overflow-hidden">
                        <img
                          src={project.image}
                          alt={project.title}
                          className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                        />
                        <div className="absolute top-2 right-2">
                          <Badge
                            variant={
                              project.status === "completed"
                                ? "default"
                                : project.status === "in-progress"
                                ? "default"
                                : "default"
                            }
                          >
                            {project.status === "completed"
                              ? "Terminé"
                              : project.status === "in-progress"
                              ? "En cours"
                              : "Pause"}
                          </Badge>
                        </div>
                      </div>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-xl">
                          {project.title}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pb-2">
                        <div className="flex items-center gap-2 mb-3">
                          <Progress
                            value={project.completion}
                            className="h-2 flex-1"
                          />
                          <span className="text-sm font-medium">
                            {project.completion}%
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-1 mb-3">
                          {project.tags.slice(0, 3).map((tag, idx) => (
                            <Badge
                              key={idx}
                              variant="outline"
                              className="text-xs"
                            >
                              {tag}
                            </Badge>
                          ))}
                          {project.tags.length > 3 && (
                            <Badge variant="outline" className="text-xs">
                              +{project.tags.length - 3}
                            </Badge>
                          )}
                        </div>
                        <CardDescription className="line-clamp-2">
                          {project.description}
                        </CardDescription>
                      </CardContent>
                      <CardFooter className="pt-0">
                        <div className="flex justify-between items-center w-full">
                          <div className="flex gap-2">
                            {project.githuburl && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(project.githuburl, "_blank");
                                }}
                              >
                                <Github className="h-4 w-4" />
                              </Button>
                            )}
                            {project.livefigma && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(project.livefigma, "_blank");
                                }}
                              >
                                <Figma className="h-4 w-4" />
                              </Button>
                            )}
                            {project.liveurl && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(project.liveurl, "_blank");
                                }}
                              >
                                <ExternalLink className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                          {/* <Button variant="ghost" size="sm" className="gap-1">
                            Details <ArrowRight className="h-3 w-3" />
                          </Button> */}
                        </div>
                      </CardFooter>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Right column - Stats */}
            <div className="space-y-6">
              {/* Team progress circle */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm text-gray-500">
                    Progression
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex justify-center">
                  <div className="relative w-32 h-32">
                    <svg className="w-full h-full" viewBox="0 0 100 100">
                      <circle
                        cx="50"
                        cy="50"
                        r="45"
                        fill="none"
                        stroke="#e2e8f0"
                        strokeWidth="10"
                      />
                      <circle
                        cx="50"
                        cy="50"
                        r="45"
                        fill="none"
                        stroke="#3b82f6"
                        strokeWidth="10"
                        strokeDasharray={`${
                          (2 * Math.PI * 45 * stats.teamProgress) / 100
                        } ${
                          (2 * Math.PI * 45 * (100 - stats.teamProgress)) / 100
                        }`}
                        strokeDashoffset={(2 * Math.PI * 45 * 25) / 100}
                        transform="rotate(-90 50 50)"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-3xl font-bold">
                        {stats.teamProgress}%
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Projects stats */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm text-gray-500">
                    Projets
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div
                      className="bg-gray-50 p-4 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => setStatusFilter(null)}
                    >
                      <p className="text-xs text-gray-500 mb-1">Total</p>
                      <p className="text-2xl font-bold">
                        {stats.totalProjects}
                      </p>
                    </div>
                    <div
                      className="bg-gray-50 p-4 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => setStatusFilter("completed")}
                    >
                      <p className="text-xs text-gray-500 mb-1">Terminé</p>
                      <p className="text-2xl font-bold text-black">
                        {stats.completedProjects}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div
                      className="bg-gray-50 p-4 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => setStatusFilter("in-progress")}
                    >
                      <p className="text-xs text-gray-500 mb-1">En cours</p>
                      <p className="text-2xl font-bold text-black">
                        {stats.inProgressProjects}
                      </p>
                    </div>
                    <div
                      className="bg-gray-50 p-4 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => setStatusFilter("paused")}
                    >
                      <p className="text-xs text-gray-500 mb-1">Pause</p>
                      <p className="text-2xl font-bold text-text">
                        {stats.waitingProjects}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
    </>
  );
};

export default Projects;
